import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import "./notifications-pop-up.scss";
import closeIcon from "../../assets/icons/closeIcon.svg";
import ellipsesIcon from "../../assets/icons/ellipsesIcon.svg";
import noReadNotificationsIcon from "../../assets/icons/noReadNotificationsIcon.svg";
import noUnreadNotificationsIcon from "../../assets/icons/noUnreadNotificationsIcon.svg";
import { ConfirmationPopup } from "../../common/components";
import ConfirmInfo from "../../assets/icons/CreateLeadCapturePopupCancelIcon.svg";
import {
  clearAllReadNotifications,
  updateNotifications,
  updateSingleNotification,
} from "../../api/notifications";
import { convertUTCToLocalRelative } from "../../utils/utils";
import notificationBellIcon from "../../assets/icons/notificationBellIcon.svg";
import { routes } from "../../constants/routes";
interface NotificationsPopupProps {
  setNotificationsPopupOpen: (open: boolean) => void;
  setHasUnreadNotification: (hasUnread: boolean) => void;
  readNotifications: any[];
  unreadNotifications: any[];
  fetchNotifications?: () => Promise<void>;
  setLoading: (loading: boolean) => void;
}
const NotificationsPopUp: React.FC<NotificationsPopupProps> = ({
  setNotificationsPopupOpen,
  setHasUnreadNotification,
  readNotifications,
  unreadNotifications,
  fetchNotifications,
  setLoading,
}) => {
  const [activeTab, setActiveTab] = useState("Unread");
  const navigate = useNavigate();
  const [isMarkPopupOpen, setIsMarkPopupOpen] = useState(false);
  const [
    markAsReadSingleNotificationPopupOpen,
    setMarkAsReadSingleNotificationPopupOpen,
  ] = useState(false);
  const [
    markAsUnreadSingleNotificationPopupOpen,
    setMarkAsUnreadSingleNotificationPopupOpen,
  ] = useState(false);
  const [clearAllNotificationsPopupOpen, setClearAllNotificationsPopupOpen] =
    useState(false);
  const [selectedNotification, setSelectedNotification] = useState<
    string | null
  >(null);
  const TAB_OPTIONS = [
    { label: "Unread", countKey: "unReadCount" },
    { label: "Read", countKey: "readCount" },
  ];
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };
  const handleMarkAllAsRead = async (markAsRead: boolean) => {
    setLoading(true);
    try {
      await updateNotifications({ markAsRead });
      if (fetchNotifications) {
        await fetchNotifications();
      }
      setIsMarkPopupOpen(false);
      setHasUnreadNotification(false);
    } catch (error) {
      // Optionally handle error
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const markSingleNotificationAsRead = (notification: any) => {
    setMarkAsReadSingleNotificationPopupOpen(true);
    setSelectedNotification(notification.id);
  };
  const markSingleNotificationAsUnread = async (notification: any) => {
    setLoading(true);
    try {
      setMarkAsUnreadSingleNotificationPopupOpen(true);
      setSelectedNotification(notification.id);
    } catch (error) {
      // Optionally handle error
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const handleUpdateSingleNotification = async (
    markAsRead: boolean,
    notificationId: string | null,
  ) => {
    if (!notificationId) return;
    setLoading(true);
    try {
      await updateSingleNotification({ markAsRead }, notificationId);
      if (fetchNotifications) {
        await fetchNotifications();
      }
      setMarkAsReadSingleNotificationPopupOpen(false);
      setMarkAsUnreadSingleNotificationPopupOpen(false);
    } catch (error) {
      // Optionally handle error
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const handleClearAllNotifications = async () => {
    setLoading(true);
    try {
      await clearAllReadNotifications();
      if (fetchNotifications) {
        await fetchNotifications();
      }
      setClearAllNotificationsPopupOpen(false);
    } catch (error) {
      // Optionally handle error
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const getNotificationIcon = () => {
    return <img src={notificationBellIcon} alt="Notification" />;
  };
  const extractName = (title: string) => {
    // Pattern: A new lead {leadName} has been created
    let match = title.match(/^A new lead (.+) has been created$/);
    if (match) return match[1];

    // Pattern: {leadName} Lead status has been updated to {Status}
    match = title.match(/^(.+) status has been updated to+$/);
    if (match) return match[1];

    match = title.match(/^(.+) lead status has been updated to (.+)+$/);
    if (match) return match[1];

    // Pattern: Lead {leadName} status has been updated to {Status}
    match = title.match(/^Lead (.+) status has been updated to +$/);
    if (match) return match[1];
    match = title.match(/^(.+) lead status has been updated to (.+)+$/);
    if (match) return match[1];
    match = title.match(/^A lead (.+) has been added to a campaign (.+)$/);
    if (match) return match[2];
    // Pattern: {count} leads have been added to a campaign {campaignName}
    match = title.match(/^(\d+) leads have been added to a campaign (.+)$/);
    if (match) return match[2];

    // Pattern: A lead {leadName} has been saved as a draft, Awaiting for your review.
    match = title.match(
      /^A lead (.+) has been saved as a draft, Awaiting for your review.$/,
    );
    if (match) return match[1];
    match = title.match(/^A new lead has been captured from the (.+)$/);
    if (match) return match[1];

    match = title.match(/^A new customer profile (.+) has been created$/);
    if (match) return match[1];

    match = title.match(/^Customer profile of (.+) details has been updated$/);
    if (match) return match[1];
    match = title.match(/^A new campaign (.+) has been created$/);
    if (match) return match[1];
    match = title.match(/^A campaign (.+) has been completed$/);
    if (match) return match[1];
    match = title.match(/^A campaign (.+) has been started$/);
    if (match) return match[1];
    match = title.match(/^A new call has been scheduled for lead (.+)$/);
    if (match) return match[1];
    match = title.match(/^A new call has been scheduled for campaign (.+)$/);
    if (match) return match[1];
    match = title.match(/^A new call has been scheduled for customer (.+)$/);
    if (match) return match[1];
    match = title.match(
      /^Your call scheduled for campaign (.+) has been cancelled$/,
    );
    if (match) return match[1];
    match = title.match(
      /^Your call scheduled for lead (.+) has been cancelled$/,
    );
    if (match) return match[1];
    match = title.match(
      /^Your call scheduled for customer (.+) has been cancelled$/,
    );
    if (match) return match[1];
    match = title.match(/^Your call with lead (.+) is overdue$/);
    if (match) return match[1];
    match = title.match(/^Your call with campaign (.+) is overdue$/);
    if (match) return match[1];
    match = title.match(/^Your call with customer (.+) is overdue$/);
    if (match) return match[1];
    match = title.match(
      /^Your call with lead (.+) is scheduled in 30 minutes$/,
    );
    if (match) return match[1];
    match = title.match(
      /^Your call with campaign (.+) is scheduled in 30 minutes$/,
    );
    if (match) return match[1];
    match = title.match(
      /^Your call with customer (.+) is scheduled in 30 minutes$/,
    );
    if (match) return match[1];
    match = title.match(/^A new lead (.+) has been assigned by AFP$/);
    if (match) return match[1];
    match = title.match(/^A meeting has been scheduled with (.+)$/);
    if (match) return match[1];
    match = title.match(/^A meeting has been scheduled for campaign (.+)$/);
    if (match) return match[1];
    match = title.match(/^A meeting has been scheduled for customer (.+)$/);
    if (match) return match[1];
    match = title.match(/^Meeting details have been updated for lead (.+)$/);
    if (match) return match[1];
    match = title.match(
      /^Meeting details have been updated for customer (.+)$/,
    );
    if (match) return match[1];
    match = title.match(
      /^Meeting details have been updated for campaign (.+)$/,
    );
    if (match) return match[1];
    match = title.match(/^Meeting has been archived with (.+)$/);
    if (match) return match[1];
    match = title.match(/^Meeting has been archived for campaign (.+)$/);
    if (match) return match[1];
    match = title.match(/^Meeting has been archived for customer (.+)$/);
    if (match) return match[1];
    match = title.match(/^A new task in lead (.+) has been assigned to you$/);
    if (match) return match[1];
    match = title.match(
      /^A new task in campaign (.+) has been assigned to you$/,
    );
    if (match) return match[1];
    match = title.match(
      /^A new task in customer (.+) has been assigned to you$/,
    );
    if (match) return match[1];
    match = title.match(/^Task details of lead (.+) have been updated$/);
    if (match) return match[1];
    match = title.match(/^Task details of campaign (.+) have been updated$/);
    if (match) return match[1];
    match = title.match(/^Task details of customer (.+) have been updated$/);
    if (match) return match[1];
    match = title.match(/^Task details (.+) in lead (.+) have been updated$/);
    if (match) return match[2];
    match = title.match(
      /^Task details (.+) in campaign (.+) have been updated$/,
    );
    if (match) return match[2];
    match = title.match(
      /^Task details (.+) in customer (.+) have been updated$/,
    );
    if (match) return match[2];
    match = title.match(/^A task in lead (.+) has been marked as completed$/);
    if (match) return match[1];
    match = title.match(
      /^A task in campaign (.+) has been marked as completed$/,
    );
    if (match) return match[1];
    match = title.match(
      /^A task in customer (.+) has been marked as completed$/,
    );
    if (match) return match[1];
    match = title.match(
      /^A task (.+) in lead (.+) has been marked as completed$/,
    );
    if (match) return match[2];
    match = title.match(
      /^A task (.+) in campaign (.+) has been marked as completed$/,
    );
    if (match) return match[2];
    match = title.match(
      /^A task (.+) in customer (.+) has been marked as completed$/,
    );
    if (match) return match[2];
    match = title.match(/^A task (.+) in lead (.+) is due today$/);
    if (match) return match[2];
    match = title.match(/^A task (.+) in campaign (.+) is due today$/);
    if (match) return match[2];
    match = title.match(/^A task (.+) in customer (.+) is due today$/);
    if (match) return match[2];
    match = title.match(/^A task (.+) in lead (.+) is overdue$/);
    if (match) return match[2];
    match = title.match(/^A task (.+) in campaign (.+) is overdue$/);
    if (match) return match[2];
    match = title.match(/^A task (.+) in customer (.+) is overdue$/);
    if (match) return match[2];
    match = title.match(/^A new call has been logged for campaign (.+)$/);
    if (match) return match[1];
    match = title.match(/^A new call has been logged for lead (.+)$/);
    if (match) return match[1];
    match = title.match(/^A new call has been logged for customer (.+)$/);
    if (match) return match[1];
    match = title.match(/^(.+) lead has been converted to a customer$/);
    if (match) return match[1];
    return "";
  };
  const handleNotificationViewClick = async (userNotification: any) => {
    setNotificationsPopupOpen(false);
    if (userNotification.is_read === false) {
      await updateSingleNotification({ markAsRead: true }, userNotification.id);
    }
    const entity = userNotification.notification.entity;
    let leadId: number | null = null;
    if (
      entity === "LEAD" ||
      entity === "LEAD_ASSIGNED" ||
      ((entity === "CALL_OVERDUE" ||
        entity === "CALL_SCHEDULED" ||
        entity === "CALL_CANCELLED" ||
        entity === "CALL_REMINDER" ||
        entity === "CALL_LOGGED") &&
        userNotification.notification.related_to_lead_id) ||
      ((entity === "CREATE_MEETING" ||
        entity === "UPDATE_MEETING" ||
        entity === "ARCHIVE_MEETING") &&
        userNotification.notification.related_to_lead_id) ||
      ((entity === "TASK_ASSIGNED" ||
        entity === "TASK_UPDATED" ||
        entity === "TASK_COMPLETED" ||
        entity === "TASK_DUE_TODAY" ||
        entity === "TASK_OVERDUE") &&
        userNotification.notification.related_to_lead_id)
    ) {
      if (
        (entity === "CALL_SCHEDULED" ||
          entity === "CALL_CANCELLED" ||
          entity === "CALL_OVERDUE" ||
          entity === "CALL_REMINDER" ||
          entity === "CALL_LOGGED") &&
        userNotification.notification.related_to_lead_id
      ) {
        leadId = userNotification.notification.related_to_lead_id;
      } else if (
        (entity === "CREATE_MEETING" ||
          entity === "UPDATE_MEETING" ||
          entity === "ARCHIVE_MEETING") &&
        userNotification.notification.related_to_lead_id
      ) {
        leadId = userNotification.notification.related_to_lead_id;
      } else if (
        (entity === "TASK_ASSIGNED" ||
          entity === "TASK_UPDATED" ||
          entity === "TASK_COMPLETED" ||
          entity === "TASK_DUE_TODAY" ||
          entity === "TASK_OVERDUE") &&
        userNotification.notification.related_to_lead_id
      ) {
        leadId = userNotification.notification.related_to_lead_id;
      } else if (entity === "LEAD" || entity === "LEAD_ASSIGNED") {
        leadId = userNotification.notification.entity_id;
      }
      const extractedName = extractName(
        userNotification.notification.message ?? "",
      );
      if (leadId) {
        navigate(routes.LEADDETAILS, {
          state: {
            selectedLeadId: leadId,
            leadName: extractedName,
            breadCrumb: true,
          },
        });
      }
    } else if (entity === "LEAD_CAPTURE_FORM") {
      const formId = userNotification.notification.entity_id;
      const extractedName = extractName(
        userNotification.notification.message ?? "",
      );
      if (formId) {
        navigate(routes.LEADCAPTUREFORMDETAILS, {
          state: {
            selectedLeadCaptureFormId: formId,
            selectedLeadCaptureFormName: extractedName,
            fromNotificationsPopup: true,
          },
        });
      }
    } else if (entity === "LEAD_STATUS" || entity === "LEADS_ASSIGNED") {
      navigate(routes.LEADLIST);
    } else if (
      entity === "CREATE_CUSTOMER" ||
      entity === "UPDATE_CUSTOMER" ||
      ([
        "CALL_OVERDUE",
        "CALL_SCHEDULED",
        "CALL_CANCELLED",
        "CALL_REMINDER",
        "CALL_LOGGED",
      ].includes(entity) &&
        userNotification.notification.related_to_customer_id) ||
      (["CREATE_MEETING", "UPDATE_MEETING", "ARCHIVE_MEETING"].includes(
        entity,
      ) &&
        userNotification.notification.related_to_customer_id) ||
      ([
        "TASK_ASSIGNED",
        "TASK_UPDATED",
        "TASK_COMPLETED",
        "TASK_DUE_TODAY",
        "TASK_OVERDUE",
      ].includes(entity) &&
        userNotification.notification.related_to_customer_id)
    ) {
      console.log(
        "Navigating to customer details with notification:",
        userNotification,
      );

      const customerId =
        entity === "CREATE_CUSTOMER" || entity === "UPDATE_CUSTOMER"
          ? userNotification.notification.entity_id
          : userNotification.notification.related_to_customer_id;

      const expectedCustomerName = extractName(
        userNotification.notification.message ?? "",
      );
      if (customerId) {
        navigate(routes.CUSTOMERDETAILS, {
          state: {
            selectedCustomerId: customerId,
            customerName: expectedCustomerName,
            breadCrumb: true,
          },
        });
      } else {
        navigate(routes.CUSTOMERLIST);
      }
    } else if (
      entity === "MASS_UPDATE_CUSTOMER" ||
      entity === "IMPORT_CUSTOMER"
    ) {
      navigate(routes.CUSTOMERLIST);
    } else if (
      entity === "CAMPAIGN_CREATED" ||
      entity === "CAMPAIGN_STATUS" ||
      entity === "CAMPAIGN_STARTED" ||
      entity === "CAMPAIGN" ||
      ((entity === "CALL_SCHEDULED" ||
        entity === "CALL_CANCELLED" ||
        entity === "CALL_OVERDUE" ||
        entity === "CALL_REMINDER" ||
        entity === "CALL_LOGGED") &&
        userNotification.notification.related_to_campaign_id) ||
      ((entity === "TASK_ASSIGNED" ||
        entity === "TASK_UPDATED" ||
        entity === "TASK_COMPLETED" ||
        entity === "TASK_DUE_TODAY" ||
        entity === "TASK_OVERDUE") &&
        userNotification.notification.related_to_campaign_id) ||
      ((entity === "CREATE_MEETING" ||
        entity === "UPDATE_MEETING" ||
        entity === "ARCHIVE_MEETING") &&
        userNotification.notification.related_to_campaign_id)
    ) {
      let campaignId: number | null = null;
      if (
        (entity === "CALL_SCHEDULED" ||
          entity === "CALL_CANCELLED" ||
          entity === "CALL_OVERDUE" ||
          entity === "CALL_REMINDER" ||
          entity === "CALL_LOGGED") &&
        userNotification.notification.related_to_campaign_id
      ) {
        campaignId = userNotification.notification.related_to_campaign_id;
      } else if (
        (entity === "TASK_ASSIGNED" ||
          entity === "TASK_UPDATED" ||
          entity === "TASK_COMPLETED" ||
          entity === "TASK_DUE_TODAY" ||
          entity === "TASK_OVERDUE") &&
        userNotification.notification.related_to_campaign_id
      ) {
        campaignId = userNotification.notification.related_to_campaign_id;
      } else if (
        entity === "CAMPAIGN_CREATED" ||
        entity === "CAMPAIGN_STATUS" ||
        entity === "CAMPAIGN_STARTED" ||
        entity === "CAMPAIGN"
      ) {
        campaignId = userNotification.notification.entity_id;
      } else if (
        (entity === "CREATE_MEETING" || entity === "UPDATE_MEETING") &&
        userNotification.notification.related_to_campaign_id
      ) {
        campaignId = userNotification.notification.related_to_campaign_id;
      }
      if (!campaignId) return;
      const extractedName = extractName(
        userNotification.notification.message ?? "",
      );
      navigate(routes.CAMPAIGNDETAILS, {
        state: {
          selectedCampaignId: campaignId,
          campaignName: extractedName,
          breadCrumb: true,
        },
      });
    } else if (
      userNotification.notification.entity === "LEAD_ASSIGNMENT_RULES"
    ) {
      navigate(routes.ASSIGNMENTRULES);
    } else if (entity === "GENERAL_NOTE") {
      navigate(routes.GENERAL_NOTES);
    }
  };
  return (
    <>
      <div className="notifications-popup">
        <div className="notifications-info-header">
          <div className="notifications-info-header-left">
            <div className="notifications-info-header-text">Notifications</div>
            <div className="unread-notification-count-div">
              {unreadNotifications.length > 0 && (
                <span
                  style={{
                    background: "#F15B59",
                    color: "#fff",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    marginLeft: 4,
                    marginRight: 0,
                    padding: 0,
                    lineHeight: 1,
                  }}
                  className="unread-notification-count"
                >
                  {unreadNotifications.length}
                </span>
              )}
            </div>
          </div>
          <img
            src={closeIcon}
            className="close-icon"
            onClick={() => {
              setNotificationsPopupOpen(false);
            }}
          />
        </div>
        <Box>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            className="mui-tabs-container"
          >
            {TAB_OPTIONS.map((tab) => {
              return (
                <Tab
                  key={tab.label}
                  sx={{
                    "&.Mui-selected": {
                      color: "#212121",
                    },
                    textTransform: "none",
                  }}
                  label={<div className="mui-tab-label">{tab.label}</div>}
                  value={tab.label}
                />
              );
            })}
          </Tabs>
          {activeTab === "Unread" && (
            <div className="notifications-list unread-list">
              {unreadNotifications.length === 0 ? (
                <div className="no-un-read-notifications">
                  <div>
                    <img
                      className="no-un-read-notifications-icon"
                      src={noUnreadNotificationsIcon}
                      alt="No Unread Notifications"
                    />
                  </div>
                  <div>
                    <h3>All caught up!</h3>
                  </div>
                  <div className="no-un-read-notifications-message">
                    <p>
                      You&apos;ve read all your notifications. Check back later
                      for updates.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="unread-notifications-container">
                    {unreadNotifications.map((eachNotification, index) => (
                      <div key={index} className="notification-item">
                        <div className="notification-module-icon">
                          {getNotificationIcon()}
                        </div>
                        <div
                          className="notification-content"
                          style={{
                            cursor: [
                              "LEAD_ARCHIVE",
                              "LEAD_IMPORT",
                              "MASS_DELETE_CUSTOMER",
                              "IMPORT_CUSTOMER",
                            ].includes(eachNotification.notification.entity)
                              ? "not-allowed"
                              : "pointer",
                          }}
                          onClick={
                            [
                              "LEAD_ARCHIVE",
                              "LEAD_IMPORT",
                              "MASS_DELETE_CUSTOMER",
                              "IMPORT_CUSTOMER",
                            ].includes(eachNotification.notification.entity)
                              ? undefined
                              : () =>
                                  handleNotificationViewClick(eachNotification)
                          }
                        >
                          <div className="notification-title">
                            {eachNotification.notification.title ||
                              "(No Title)"}
                          </div>
                          <div className="notification-message">
                            {eachNotification.notification.message ||
                              "(No Message)"}
                          </div>
                          <div className="notification-date">
                            {eachNotification.notification.created_at
                              ? convertUTCToLocalRelative(
                                  eachNotification.notification.created_at,
                                )
                              : ""}
                          </div>
                        </div>
                        <div className="mark-as-read-icon">
                          <img
                            src={ellipsesIcon}
                            alt="Options"
                            onClick={() =>
                              markSingleNotificationAsRead(eachNotification)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mark-all-as-read">
                    <h4 onClick={() => setIsMarkPopupOpen(true)}>
                      Mark all as read
                    </h4>
                  </div>
                </>
              )}
            </div>
          )}
          {activeTab === "Read" && (
            <div className="notifications-list read-list">
              {readNotifications.length === 0 ? (
                <div className="no-read-notifications">
                  <div>
                    <img
                      className="no-read-notifications-icon"
                      src={noReadNotificationsIcon}
                      alt="No Read Notifications"
                    />
                  </div>
                  <div>
                    <h3>No Notifications</h3>
                  </div>
                  <div className="no-read-notifications-message">
                    <p>
                      No notifications to show right now. We&apos;ll notify you
                      when something needs your attention.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="read-notifications-container">
                    {readNotifications.map((eachNotification, index) => (
                      <div key={index} className="notification-item">
                        <div className="notification-module-icon">
                          {getNotificationIcon()}
                        </div>
                        <div
                          className="notification-content"
                          style={{
                            cursor: [
                              "LEAD_ARCHIVE",
                              "LEAD_IMPORT",
                              "MASS_DELETE_CUSTOMER",
                              "IMPORT_CUSTOMER",
                            ].includes(eachNotification.notification.entity)
                              ? "not-allowed"
                              : "pointer",
                          }}
                          onClick={
                            [
                              "LEAD_ARCHIVE",
                              "LEAD_IMPORT",
                              "MASS_DELETE_CUSTOMER",
                              "IMPORT_CUSTOMER",
                            ].includes(eachNotification.notification.entity)
                              ? undefined
                              : () =>
                                  handleNotificationViewClick(eachNotification)
                          }
                        >
                          <div className="notification-title">
                            {eachNotification.notification?.title ||
                              "(No Title)"}
                          </div>
                          <div className="notification-message">
                            {eachNotification.notification?.message ||
                              "(No Message)"}
                          </div>
                          <div className="notification-date">
                            {eachNotification.notification?.created_at
                              ? convertUTCToLocalRelative(
                                  eachNotification.notification.created_at,
                                )
                              : ""}
                          </div>
                        </div>
                        <div className="mark-as-un-read-icon">
                          <img
                            src={ellipsesIcon}
                            alt="Options"
                            onClick={() =>
                              markSingleNotificationAsUnread(eachNotification)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="clear-all-notifications">
                    <h4 onClick={() => setClearAllNotificationsPopupOpen(true)}>
                      Clear All
                    </h4>
                  </div>
                </>
              )}
            </div>
          )}
        </Box>
      </div>
      <div className="mark-completed">
        <ConfirmationPopup
          open={isMarkPopupOpen}
          onCancel={() => setIsMarkPopupOpen(false)}
          onOkay={() => handleMarkAllAsRead(true)}
          title="Mark All As Read"
          note="This will mark all unread notifications as read."
          buttonText="Confirm"
          Icon={ConfirmInfo}
          className="mark-completed-confirmation"
        />
      </div>
      <div className="mark-completed">
        <ConfirmationPopup
          open={markAsReadSingleNotificationPopupOpen}
          onCancel={() => setMarkAsReadSingleNotificationPopupOpen(false)}
          onOkay={() =>
            handleUpdateSingleNotification(true, selectedNotification)
          }
          title="Mark As Read"
          note="This will mark the selected notification as read."
          buttonText="Confirm"
          Icon={ConfirmInfo}
          className="mark-completed-confirmation"
        />
      </div>
      <div className="mark-completed">
        <ConfirmationPopup
          open={markAsUnreadSingleNotificationPopupOpen}
          onCancel={() => setMarkAsUnreadSingleNotificationPopupOpen(false)}
          onOkay={() =>
            handleUpdateSingleNotification(false, selectedNotification)
          }
          title="Mark As Unread"
          note="This will mark the selected notification as unread."
          buttonText="Confirm"
          Icon={ConfirmInfo}
          className="mark-completed-confirmation"
        />
      </div>
      <div className="mark-completed">
        <ConfirmationPopup
          open={clearAllNotificationsPopupOpen}
          onCancel={() => setClearAllNotificationsPopupOpen(false)}
          onOkay={handleClearAllNotifications}
          title="Clear all notifications?"
          note="This will permanently remove all notifications from the list."
          buttonText="Confirm"
          Icon={ConfirmInfo}
          className="mark-completed-confirmation"
        />
      </div>
    </>
  );
};

export default NotificationsPopUp;
